import React from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCheckoutStore } from "../store/checkout-store";
import { cn } from "@/lib/utils";

export const CheckoutStepper: React.FC = () => {
  const { t } = useTranslation();
  const {
    selectedDeliveryAddressId,
    deliveryAddressFormStatus,
    isAddingNewAddress,
    isEditingDeliveryAddress,
    cardMode,
    selectedSavedCardToken,
    newCardData,
  } = useCheckoutStore();

  const isDeliveryFormOpen = isAddingNewAddress || isEditingDeliveryAddress;
  const isDeliveryDone =
    Boolean(selectedDeliveryAddressId) &&
    !isDeliveryFormOpen &&
    deliveryAddressFormStatus === "closed";
  const isPaymentDone =
    (cardMode === "saved" && Boolean(selectedSavedCardToken)) ||
    (cardMode === "new" && Boolean(newCardData));

  const steps = [
    { key: "delivery", label: t("checkout.stepDelivery"), done: isDeliveryDone },
    { key: "payment", label: t("checkout.stepPayment"), done: isPaymentDone },
    { key: "confirm", label: t("checkout.stepConfirm"), done: false },
  ];

  const activeIndex = steps.findIndex((step) => !step.done);

  return (
    <ol className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
      {steps.map((step, index) => {
        const isActive = index === (activeIndex === -1 ? steps.length - 1 : activeIndex);
        const isComplete = step.done;

        return (
          <li key={step.key} className="flex items-center flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={cn(
                  "flex items-center justify-center w-6 h-6 md:w-7 md:h-7 rounded-full text-[11px] md:text-xs font-semibold shrink-0",
                  isComplete && "bg-primary text-primary-foreground",
                  isActive && !isComplete && "bg-primary text-primary-foreground",
                  !isActive && !isComplete && "bg-bg-secondary dark:bg-gray-800 text-text-tertiary dark:text-gray-400 border border-border-secondary dark:border-gray-600"
                )}
              >
                {isComplete ? <Check className="w-3.5 h-3.5" /> : index + 1}
              </span>
              <span
                className={cn(
                  "text-xs md:text-sm font-medium truncate",
                  isComplete || isActive
                    ? "text-text-primary dark:text-text-primary"
                    : "text-text-tertiary dark:text-gray-400"
                )}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "flex-1 h-px mx-2 md:mx-3",
                  isComplete
                    ? "bg-primary"
                    : "bg-border-secondary dark:bg-gray-700"
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
};
